/* eslint-disable @typescript-eslint/no-explicit-any */
import { GetServerSideProps } from 'next';
import { inspect } from 'util';
import { ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { ApolloProvider } from '@apollo/client';

import { respGridUtil, getProcessEnvs, logger } from '@marriott/mi-headless-utils';
import { getDeployedEnvType } from '@marriott/mi-apollo-client-utils';
import { constants, MBOP_FEATURE_FLAG_HEADER } from '@marriott/mi-account-components/constants';
import { fetchFlags, preProcessorCall } from '@marriott/mi-account-components/utils';
import { useMiApolloClient } from '@marriott/mi-apollo-client-utils';
import { prePageCall, Layout } from '@marriott/mi-headless-utils';
import { FEATURE_FLAGS, TARGETS } from '@marriott/mi-feature-flag-utils';
import { promotionsDetailModel } from '../../../_localDevMocks';
import operationSignatures from '../../../_constants/operationSignatures.json';

const { NEXT_PUBLIC_AEM_PATH, SKIP_PREPROCESSOR_CALL, IS_LOCAL_DEV } = getProcessEnvs();
const { ITEM_PATH_ROOT, PROMOTION_PREPROCESSOR_URL, NEXT_PUBLIC_ENV_KEYS } = constants;

global.loggerInstance = logger({}) as CallableFunction;
const { log } = global.loggerInstance('PromotionCentralPage');

export default function Page({ model, deployedEnvType }) {
  const MiApolloClient = useMiApolloClient(operationSignatures ?? [], deployedEnvType);

  const {
    metaNames,
    metaProperties,
    title,
    staticComponentClientLibsCSS,
    staticComponentClientLibsJS,
    gridCss,
    authorMode,
    canonical,
  } = model;
  const responsiveGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid);

  return (
    <ApolloProvider client={MiApolloClient}>
      <Layout
        metaNames={metaNames}
        metaProps={metaProperties}
        title={title}
        staticComponentClientLibsCSS={staticComponentClientLibsCSS}
        staticComponentClientLibsJS={staticComponentClientLibsJS}
        gridCss={gridCss}
        isAuthorMode={authorMode}
        canonical={canonical}
      >
        <ResponsiveGrid
          {...responsiveGridModel}
          model={responsiveGridModel}
          pagePath={NEXT_PUBLIC_AEM_PATH}
          itemPath={ITEM_PATH_ROOT}
          customClassName="account-page-wrapper"
        />
      </Layout>
    </ApolloProvider>
  );
}
const accountPromotionalDetailSpecificData = async request => {
  const deployedEnvType = getDeployedEnvType();
  let response: any = { operationSignatures: [], deployedEnvType };
  const resolvedUrlString: string = request?.req?.url;
  const isAuthorMode = resolvedUrlString?.includes('isAuthorMode');
  const urlQueryParameters = new URLSearchParams(request?.query)?.toString();
  const featureFlags = await fetchFlags(
    [
      FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ENABLE_MBOP_PROMOTIONS,
      FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ENABLE_PROMOTION_TIMER,
    ],
    {
      target: TARGETS.APP.ACCOUNT_RENDERER,
    }
  );

  /**
   * Preprocessor Call Start
   */
  const promotionPath = urlQueryParameters
    ? `${PROMOTION_PREPROCESSOR_URL}?${urlQueryParameters}`
    : PROMOTION_PREPROCESSOR_URL;

  if (SKIP_PREPROCESSOR_CALL !== 'true' && IS_LOCAL_DEV !== 'true' && !isAuthorMode) {
    // Making page specific preprocessor call
    log.debug(`[accountPromotionBannerPageSpecificData] session Id: ${request?.req?.cookies?.sessionID}`);
    const featureFlagPromotion = Boolean(featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_ENABLE_MBOP_PROMOTIONS]);
    const preprocessorResponse = await preProcessorCall(
      request?.req?.cookies?.sessionID,
      request?.req,
      promotionPath,
      featureFlagPromotion ? MBOP_FEATURE_FLAG_HEADER : {}
    );
    log.debug(`[accountPromotionBannerPageSpecificData] PreprocessorResponse: ${inspect(preprocessorResponse)}`);
    if (preprocessorResponse?.data?.error) {
      const error = {
        redirect: { redirect: preprocessorResponse?.data?.redirect },
        value: true,
      };
      response = { operationSignatures: [], deployedEnvType, error };
    } else if (preprocessorResponse?.data) {
      if (preprocessorResponse?.data?.redirect) {
        const error = {
          redirect: {
            redirect: {
              destination: preprocessorResponse?.data?.nextStateURI,
              permanent: false,
            },
          },
          value: true,
        };
        response = {
          operationSignatures: [],
          deployedEnvType,
          error,
          nextPublicEnvKeys: NEXT_PUBLIC_ENV_KEYS,
        };
      } else {
        response = {
          operationSignatures: [],
          deployedEnvType,
          userState: preprocessorResponse?.data?.userState,
          nextPublicEnvKeys: NEXT_PUBLIC_ENV_KEYS,
        };
      }
    }
  }
  /**
   * Preprocessor Call End
   */

  return {
    ...response,
    featureFlags,
    featureFlagLogo: featureFlags,
  };
};

export const getServerSideProps: GetServerSideProps<any> = prePageCall(
  accountPromotionalDetailSpecificData,
  promotionsDetailModel
);
